import { z } from 'zod';

/**
 * Source error reports (M5, ADR-026): a reader tells the editorial team that a document, its
 * extracted text or a citation of it looks wrong. A report never changes a verdict; editors
 * check it against the original and record what they found.
 */

export const SourceReportKindSchema = z.enum([
  'text_error',
  'wrong_document',
  'citation_problem',
  'outdated_law',
  'other',
]);
export type SourceReportKind = z.infer<typeof SourceReportKindSchema>;

export const SourceReportContextSchema = z.enum(['library', 'answer', 'memo']);

export const CreateSourceReportRequestSchema = z
  .object({
    versionId: z.string().regex(/^[A-Za-z0-9_-]{1,200}$/),
    passageId: z
      .string()
      .regex(/^[A-Za-z0-9_~-]{1,200}$/)
      .optional(),
    kind: SourceReportKindSchema,
    description: z.string().trim().min(10).max(2000),
    context: SourceReportContextSchema,
  })
  .strict();
export type CreateSourceReportRequest = z.infer<typeof CreateSourceReportRequestSchema>;

export const SourceReportSchema = z.object({
  id: z.string(),
  versionId: z.string(),
  title: z.string(),
  collection: z.enum(['acts', 'cases']),
  passageId: z.string().nullable(),
  kind: SourceReportKindSchema,
  description: z.string(),
  context: SourceReportContextSchema,
  reporterUid: z.string(),
  reporterName: z.string().nullable(),
  createdAt: z.string(),
  status: z.enum(['open', 'resolved', 'dismissed']),
  resolution: z
    .object({
      note: z.string(),
      byUid: z.string(),
      byName: z.string().nullable(),
      at: z.string(),
    })
    .nullable(),
});
export type SourceReport = z.infer<typeof SourceReportSchema>;

export const SourceReportListSchema = z.object({ reports: z.array(SourceReportSchema) });
export type SourceReportList = z.infer<typeof SourceReportListSchema>;

export const ResolveSourceReportRequestSchema = z
  .object({
    status: z.enum(['resolved', 'dismissed']),
    note: z.string().trim().min(3).max(2000),
  })
  .strict();
export type ResolveSourceReportRequest = z.infer<typeof ResolveSourceReportRequestSchema>;

/** Reports one account may send in a day: enough to report real problems, not to flood. */
export const DAILY_REPORT_LIMIT = 10;
